import { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Save, Loader2 } from 'lucide-react';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { useApp } from '../../context/AppContext';
import { getCompanyVacationDates } from '../../utils/companyVacation';
import type { CompanyVacationRule, WageType } from '../../types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const MONTH_NAMES = [
  '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function formatPreviewDate(year: number, month: number, day: number): string {
  const date = new Date(year, month - 1, day);
  const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year} (${weekday})`;
}

export function CompanyVacationSettings() {
  const { getCompanyVacationRule, saveCompanyVacationRule, getMe } = useAPIStorage();
  const { state, applyCompanyVacation } = useApp();

  const [rule, setRule] = useState<CompanyVacationRule | null>(null);
  const [myWageType, setMyWageType] = useState<WageType | null>(null);
  const [startDay, setStartDay] = useState(24);
  const [startMonth, setStartMonth] = useState(12);
  const [endDay, setEndDay] = useState(1);
  const [endMonth, setEndMonth] = useState(1);
  const [active, setActive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const loadRule = useCallback(async () => {
    try {
      const [data, me] = await Promise.all([getCompanyVacationRule(), getMe()]);
      setRule(data);
      setMyWageType(me?.wageType ?? null);
      if (data) {
        setStartDay(data.startDay);
        setStartMonth(data.startMonth);
        setEndDay(data.endDay);
        setEndMonth(data.endMonth);
        setActive(data.active);
      }
    } catch (e) {
      console.error('Fehler beim Laden der Betriebsferien-Regel:', e);
    } finally {
      setIsLoading(false);
    }
  }, [getCompanyVacationRule, getMe]);

  useEffect(() => {
    void loadRule();
  }, [loadRule]);

  // Änderungs-Tracking
  useEffect(() => {
    if (!rule) {
      setHasChanges(true);
      return;
    }
    setHasChanges(
      startDay !== rule.startDay ||
      startMonth !== rule.startMonth ||
      endDay !== rule.endDay ||
      endMonth !== rule.endMonth ||
      active !== rule.active
    );
  }, [rule, startDay, startMonth, endDay, endMonth, active]);

  // Vorschau berechnen
  const preview = useMemo(() => {
    const previewRule: CompanyVacationRule = {
      id: '',
      startDay,
      startMonth,
      endDay,
      endMonth,
      active,
    };
    const dates = getCompanyVacationDates(previewRule, state.year);
    return {
      dates,
      count: dates.size,
      isYearSpanning: endMonth < startMonth,
    };
  }, [startDay, startMonth, endDay, endMonth, active, state.year]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await saveCompanyVacationRule({
        startDay,
        startMonth,
        endDay,
        endMonth,
        active,
      });
      setRule(saved);

      // Betriebsferien im eigenen Kalender aktualisieren (basierend auf eigenem WageType)
      const dates = getCompanyVacationDates(saved, state.year);
      applyCompanyVacation(dates, myWageType);
    } catch (e) {
      console.error('Fehler beim Speichern der Betriebsferien-Regel:', e);
      alert('Fehler beim Speichern: ' + (e instanceof Error ? e.message : 'Unbekannter Fehler'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Lade Betriebsferien...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-gray-100 dark:bg-gray-800 p-1.5">
            <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              Betriebsferien
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Wiederkehrender Zeitraum, gilt für alle Mitarbeiter
            </p>
          </div>
        </div>

        {/* Aktiv-Toggle */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="cv-active"
            checked={active}
            onCheckedChange={(checked) => setActive(checked === true)}
          />
          <Label htmlFor="cv-active" className="text-sm font-medium cursor-pointer">
            Betriebsferien aktiv
          </Label>
        </div>

        {/* Zeitraum */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Von</Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={1}
                max={31}
                value={startDay}
                onChange={(e) => setStartDay(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
              />
              <span className="text-gray-500">.</span>
              <Input
                type="number"
                min={1}
                max={12}
                value={startMonth}
                onChange={(e) => setStartMonth(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
              />
              <span className="text-xs text-gray-400 ml-1">{MONTH_NAMES[startMonth]}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Bis</Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={1}
                max={31}
                value={endDay}
                onChange={(e) => setEndDay(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
              />
              <span className="text-gray-500">.</span>
              <Input
                type="number"
                min={1}
                max={12}
                value={endMonth}
                onChange={(e) => setEndMonth(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
                className="w-16 text-center"
              />
              <span className="text-xs text-gray-400 ml-1">{MONTH_NAMES[endMonth]}</span>
            </div>
          </div>
        </div>

        {/* Vorschau */}
        {active && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vorschau {state.year}:
            </p>
            {preview.count > 0 ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {preview.isYearSpanning
                    ? `${formatPreviewDate(state.year - 1, startMonth, startDay)} – ${formatPreviewDate(state.year, endMonth, endDay)}`
                    : `${formatPreviewDate(state.year, startMonth, startDay)} – ${formatPreviewDate(state.year, endMonth, endDay)}`
                  }
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  = <span className="font-semibold tabular-nums">{preview.count}</span> Werktage im Jahr {state.year}
                  {' '}(Feiertage nicht abgezogen)
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Keine Werktage in diesem Zeitraum
              </p>
            )}
          </div>
        )}

        {/* Speichern */}
        <Button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          size="sm"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Speichern
        </Button>
      </CardContent>
    </Card>
  );
}
